import type { GridGuideArrowVisibility } from './gridGuideArrows';

export type GridGuideModalAction = 'left' | 'right' | 'up' | 'down' | 'done';

export interface GridGuideModalRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface GridGuideModalLayout {
  modal: GridGuideModalRect;
  left: GridGuideModalRect;
  right: GridGuideModalRect;
  up: GridGuideModalRect;
  down: GridGuideModalRect;
  done: GridGuideModalRect;
}

export interface NumberLineGridGuideModalLayout {
  modal: GridGuideModalRect;
  left?: GridGuideModalRect;
  right?: GridGuideModalRect;
  up?: GridGuideModalRect;
  down?: GridGuideModalRect;
  done: GridGuideModalRect;
}

interface CoordinateGridGuideModalLayoutOptions {
  canvasWidth: number;
  grid: GridGuideModalRect;
  margin?: number;
}

interface NumberLineGridGuideModalLayoutOptions {
  arrows: GridGuideArrowVisibility;
  canvasWidth: number;
  canvasHeight: number;
  grid: GridGuideModalRect;
  submitRowY?: number;
  margin?: number;
}

export const HORIZONTAL_NUMBER_LINE_MODAL_HEIGHT = 56;
export const VERTICAL_NUMBER_LINE_MODAL_HEIGHT = 104;
export const GRID_GUIDE_MODAL_MARGIN = 8;
export const GRID_GUIDE_MODAL_ACTIONS: readonly GridGuideModalAction[] = [
  'left',
  'right',
  'up',
  'down',
  'done',
];

const GRID_GUIDE_BUTTON_SIZE = 40;
const DONE_BUTTON_WIDTH = 72;
const COORDINATE_MODAL_HEIGHT = 108;

const clamp = (value: number, minimum: number, maximum: number): number =>
  Math.max(minimum, Math.min(value, maximum));

const containsPoint = (
  rectangle: GridGuideModalRect,
  x: number,
  y: number
): boolean =>
  x >= rectangle.x &&
  x <= rectangle.x + rectangle.w &&
  y >= rectangle.y &&
  y <= rectangle.y + rectangle.h;

export const getNumberLineGridGuideModalLayout = ({
  arrows,
  canvasWidth,
  canvasHeight,
  grid,
  submitRowY,
  margin = GRID_GUIDE_MODAL_MARGIN,
}: NumberLineGridGuideModalLayoutOptions): NumberLineGridGuideModalLayout => {
  const isVertical = arrows.up;
  const modalWidth = Math.min(
    isVertical ? 136 : Math.max(280, grid.w),
    canvasWidth - 2 * margin
  );
  const modalHeight = isVertical
    ? VERTICAL_NUMBER_LINE_MODAL_HEIGHT
    : HORIZONTAL_NUMBER_LINE_MODAL_HEIGHT;
  const maxLeft = Math.max(margin, canvasWidth - modalWidth - margin);
  const maxTop = Math.max(margin, canvasHeight - modalHeight - margin);
  let modalX: number;
  let modalY: number;

  if (isVertical) {
    const gap = 12;
    const gridRight = grid.x + grid.w;
    const fitsRight = gridRight + gap + modalWidth <= canvasWidth - margin;

    modalX = fitsRight ? gridRight + gap : grid.x - modalWidth - gap;
    modalY = grid.y + (grid.h - modalHeight) / 2;
  } else {
    modalX = grid.x + (grid.w - modalWidth) / 2;
    modalY = submitRowY ?? grid.y + grid.h + 5;
  }

  modalX = clamp(modalX, margin, maxLeft);
  modalY = clamp(modalY, margin, maxTop);

  const modal = {
    x: modalX,
    y: modalY,
    w: modalWidth,
    h: modalHeight,
  };

  if (isVertical) {
    const leftColumnX = modalX + 12;

    return {
      modal,
      up: {
        x: leftColumnX,
        y: modalY + 8,
        w: GRID_GUIDE_BUTTON_SIZE,
        h: GRID_GUIDE_BUTTON_SIZE,
      },
      down: {
        x: leftColumnX,
        y: modalY + 56,
        w: GRID_GUIDE_BUTTON_SIZE,
        h: GRID_GUIDE_BUTTON_SIZE,
      },
      done: {
        x: modalX + modalWidth - DONE_BUTTON_WIDTH - 8,
        y: modalY + 32,
        w: DONE_BUTTON_WIDTH,
        h: GRID_GUIDE_BUTTON_SIZE,
      },
    };
  }

  const buttonGap = 16;
  const buttonGroupWidth = GRID_GUIDE_BUTTON_SIZE * 2 + buttonGap;
  const arrowButtonGroupX = modalX + modalWidth * 0.25 - buttonGroupWidth / 2;
  const buttonY = modalY + 8;

  return {
    modal,
    left: {
      x: arrowButtonGroupX,
      y: buttonY,
      w: GRID_GUIDE_BUTTON_SIZE,
      h: GRID_GUIDE_BUTTON_SIZE,
    },
    right: {
      x: arrowButtonGroupX + GRID_GUIDE_BUTTON_SIZE + buttonGap,
      y: buttonY,
      w: GRID_GUIDE_BUTTON_SIZE,
      h: GRID_GUIDE_BUTTON_SIZE,
    },
    done: {
      x: modalX + modalWidth * 0.75 - DONE_BUTTON_WIDTH / 2,
      y: buttonY,
      w: DONE_BUTTON_WIDTH,
      h: GRID_GUIDE_BUTTON_SIZE,
    },
  };
};

export const getCoordinateGridGuideModalLayout = ({
  canvasWidth,
  grid,
  margin = GRID_GUIDE_MODAL_MARGIN,
}: CoordinateGridGuideModalLayoutOptions): GridGuideModalLayout => {
  const gap = 10;
  const modalWidth = Math.min(Math.max(280, grid.w), canvasWidth - 2 * margin);
  const modalX = clamp(
    grid.x + (grid.w - modalWidth) / 2,
    margin,
    canvasWidth - modalWidth - margin
  );
  const modalY = grid.y + grid.h + gap;
  const buttonSize = Math.min(GRID_GUIDE_BUTTON_SIZE, (modalWidth - 36) / 6);
  const dpadCenterX = modalX + modalWidth * 0.3;
  const topButtonY = modalY + 8;
  const middleButtonY = modalY + 34;
  const bottomButtonY = modalY + 60;

  return {
    modal: {
      x: modalX,
      y: modalY,
      w: modalWidth,
      h: COORDINATE_MODAL_HEIGHT,
    },
    up: {
      x: dpadCenterX - buttonSize / 2,
      y: topButtonY,
      w: buttonSize,
      h: buttonSize,
    },
    left: {
      x: dpadCenterX - buttonSize - 26,
      y: middleButtonY,
      w: buttonSize,
      h: buttonSize,
    },
    right: {
      x: dpadCenterX + 26,
      y: middleButtonY,
      w: buttonSize,
      h: buttonSize,
    },
    down: {
      x: dpadCenterX - buttonSize / 2,
      y: bottomButtonY,
      w: buttonSize,
      h: buttonSize,
    },
    done: {
      x: modalX + modalWidth * 0.75 - DONE_BUTTON_WIDTH / 2,
      y: middleButtonY,
      w: DONE_BUTTON_WIDTH,
      h: buttonSize,
    },
  };
};

export const getGridGuideModalActionAtPoint = (
  layout: Partial<Record<GridGuideModalAction, GridGuideModalRect>>,
  x: number,
  y: number
): GridGuideModalAction | undefined => {
  return GRID_GUIDE_MODAL_ACTIONS.find(action => {
    const button = layout[action];

    return button !== undefined && containsPoint(button, x, y);
  });
};

export const isPointInGridGuideModal = (
  layout: { modal: GridGuideModalRect },
  x: number,
  y: number
): boolean => containsPoint(layout.modal, x, y);
